/**
 * Утилиты для работы с тестами и прогрессом пользователя
 */

/**
 * Получает ID теста в строковом формате
 * @param {string|Object} testId - ID теста
 * @returns {string} ID теста в строковом формате
 */
export const getTestId = (testId) => {
  return typeof testId === 'string' ? testId : testId.toString();
};

/**
 * Проверяет, пройден ли тест (завершен И пройден)
 * @param {Object} test - объект теста
 * @param {Object} userProgress - прогресс пользователя
 * @returns {boolean} true если тест пройден
 */
export const isTestPassed = (test, userProgress) => {
  if (!test || !userProgress) return false;
  
  const testId = getTestId(test._id);
  const completedTestIds = userProgress?.completedTests || [];
  const testResults = userProgress?.testResults || {};
  
  const isCompleted = completedTestIds.includes(testId);
  const testResultData = testResults[testId];
  
  return isCompleted && testResultData && testResultData.passed;
};

/**
 * Проверяет, завершен ли тест (независимо от результата)
 * @param {Object} test - объект теста
 * @param {Object} userProgress - прогресс пользователя
 * @returns {boolean} true если тест завершен
 */
export const isTestCompleted = (test, userProgress) => {
  if (!test || !userProgress) return false;
  
  const testId = getTestId(test._id);
  const completedTestIds = userProgress?.completedTests || [];
  
  return completedTestIds.includes(testId);
};

/**
 * Получает статус и результат теста
 * @param {Object} test - объект теста
 * @param {Object} userProgress - прогресс пользователя
 * @returns {Object} объект с полями status и result
 */
export const getTestStatusAndResult = (test, userProgress) => {
  if (!test || !userProgress) {
    return { status: 'available', result: null };
  }

  const testId = getTestId(test._id);
  const completedTestIds = userProgress?.completedTests || [];
  const isTestCompleted = completedTestIds.includes(testId);

  if (isTestCompleted) {
    const testResultData = userProgress?.testResults?.[testId];
    if (testResultData) {
      return {
        status: testResultData.passed ? 'passed' : 'failed',
        result: testResultData
      };
    }
  }

  return { status: 'available', result: null };
};

/**
 * Проверяет, пройдены ли все тесты урока
 * @param {Object} lesson - объект урока
 * @param {Object} userProgress - прогресс пользователя
 * @returns {boolean} true если все тесты пройдены
 */
export const areAllTestsPassed = (lesson, userProgress) => {
  if (!lesson.tests || !Array.isArray(lesson.tests) || lesson.tests.length === 0) {
    return true; // Если тестов нет, считаем что все пройдено
  }

  return lesson.tests.every(test => isTestPassed(test, userProgress));
};

/**
 * Подсчитывает количество пройденных тестов
 * @param {Array} tests - массив тестов
 * @param {Object} userProgress - прогресс пользователя
 * @returns {number} количество пройденных тестов
 */
export const getPassedTestsCount = (tests, userProgress) => {
  if (!tests || !Array.isArray(tests)) return 0;
  
  return tests.filter(test => isTestPassed(test, userProgress)).length;
};

/**
 * Получает статус урока на основе его тестов
 * @param {Object} lesson - объект урока
 * @param {Object} userProgress - прогресс пользователя
 * @param {boolean} isLessonCompleted - завершен ли урок
 * @returns {string} статус урока
 */
export const getLessonStatus = (lesson, userProgress, isLessonCompleted) => {
  if (!isLessonCompleted) return 'available';
  
  // Проверяем, есть ли у урока тесты
  if (lesson.tests && Array.isArray(lesson.tests) && lesson.tests.length > 0) {
    // Проверяем, пройдены ли ВСЕ тесты
    const allTestsPassed = areAllTestsPassed(lesson, userProgress);
    
    if (allTestsPassed) {
      return 'test_passed';
    } else {
      // Проверяем, есть ли хотя бы один проваленный тест
      const hasFailedTest = lesson.tests.some(test => {
        const testId = getTestId(test._id);
        const completedTestIds = userProgress?.completedTests || [];
        const testResults = userProgress?.testResults || {};
        
        const isCompleted = completedTestIds.includes(testId);
        const testResultData = testResults[testId];
        
        return isCompleted && testResultData && !testResultData.passed;
      });
      
      return hasFailedTest ? 'test_failed' : 'completed_with_test';
    }
  }
  
  // Если у урока нет тестов - он считается завершенным
  return 'completed';
};

/**
 * Форматирует длительность в минутах
 * @param {number} minutes - количество минут
 * @param {Function} t - функция перевода
 * @returns {string} отформатированная длительность
 */
export const formatDuration = (minutes, t) => {
  if (!minutes) return 'N/A';
  return `${minutes} ${t('common:time.shortMin')}`;
};
